// TokenProvider — the ONE gate every piece of auth material flows through. localStorage
// is banned by lint; both MVP apps stored bearers there (a named defect that caused a mass-logout bug and a
// wrong-account login). The contract:
//   • Native (iOS): the opaque bearer lives in the iOS Keychain (capacitor-secure-storage-plugin) and is
//     mirrored in memory for synchronous reads; it survives app relaunch.
//   • Web: the bearer is in MEMORY ONLY. Persistence across reloads is the httpOnly Path-scoped reissue
//     cookie (T-08) — the app boots → calls /auth/reissue → setBearer(). Nothing auth-shaped touches
//     localStorage/sessionStorage.
//   • clear() runs ONLY on an explicit 401, on logout, and on switchBand() — NEVER on 5xx/network (the
//     Antenna fetchMe mass-logout bug). It purges memory AND the Keychain.
//   • WS tickets, API auth, and push registration all read the bearer THROUGH this provider.
import { Capacitor } from "@capacitor/core";
import { SecureStoragePlugin } from "capacitor-secure-storage-plugin";

/** The Keychain key for the opaque bearer. Stable — changing it orphans stored sessions. */
const BEARER_KEY = "cntrl.session.bearer";

/**
 * The auth-material interface. Synchronous {@link getBearer} (in-memory) keeps the fetch/ws/push hot paths
 * allocation-free; the async methods touch the Keychain on native.
 */
export interface TokenProvider {
  /** The current bearer, or null if unauthenticated. Synchronous, in-memory. */
  getBearer(): string | null;
  /** True iff a bearer is currently held. */
  isAuthenticated(): boolean;
  /** Load any persisted bearer at boot (native → Keychain; web → no-op — boot reissue supplies it). */
  load(): Promise<void>;
  /** Persist a freshly-issued bearer (native → Keychain + memory; web → memory). */
  setBearer(token: string): Promise<void>;
  /**
   * Set the in-memory bearer WITHOUT persisting it. On web this is identical to {@link setBearer} (the bearer
   * is memory-only there regardless). On native it is the whole point: the Keychain — the relaunch anchor —
   * is left holding the caller's REAL token, so a worn bearer never survives a process death. This is the
   * native analogue of the web reissue cookie: kill the app mid-swap and `load()` restores the real token,
   * exactly as a browser reload does. Used ONLY by the dev fixture switcher; never by login / reissue.
   */
  setBearerEphemeral(token: string): Promise<void>;
  /** Purge ALL auth material — memory + Keychain. Call ONLY on 401 / logout / switchBand (never 5xx/network). */
  clear(): Promise<void>;
}

/** Web: bearer in memory only; reload-persistence is the reissue cookie.*/
export class WebTokenProvider implements TokenProvider {
  private bearer: string | null = null;

  getBearer(): string | null {
    return this.bearer;
  }
  isAuthenticated(): boolean {
    return this.bearer !== null;
  }
  async load(): Promise<void> {
    // No-op on web: nothing is persisted client-side. The app's bootstrap calls POST /auth/reissue and,
    // on success, setBearer() with the fresh in-memory bearer.
  }
  async setBearer(token: string): Promise<void> {
    this.bearer = token;
  }
  async setBearerEphemeral(token: string): Promise<void> {
    // Web keeps no client-side persistence, so ephemeral === setBearer here. Distinct for a uniform interface.
    this.bearer = token;
  }
  async clear(): Promise<void> {
    this.bearer = null;
  }
}

/** Native (iOS): bearer in the Keychain, mirrored in memory. Survives relaunch; purged on clear(). */
export class NativeTokenProvider implements TokenProvider {
  private bearer: string | null = null;

  getBearer(): string | null {
    return this.bearer;
  }
  isAuthenticated(): boolean {
    return this.bearer !== null;
  }
  async load(): Promise<void> {
    try {
      const { value } = await SecureStoragePlugin.get({ key: BEARER_KEY });
      this.bearer = value ?? null;
    } catch {
      // The plugin THROWS when the key is absent (first launch / after clear) — that is "unauthenticated",
      // not an error to surface. Never clears anything on a read miss.
      this.bearer = null;
    }
  }
  async setBearer(token: string): Promise<void> {
    this.bearer = token;
    await SecureStoragePlugin.set({ key: BEARER_KEY, value: token });
  }
  async setBearerEphemeral(token: string): Promise<void> {
    // Memory ONLY, on purpose: the Keychain keeps the caller's real token, so a relaunch mid-swap resolves
    // back to them via load(). Writing here would strand a tester as the worn account with no way back —
    // which is exactly why the fixture switcher used to be disabled on native.
    this.bearer = token;
  }
  async clear(): Promise<void> {
    this.bearer = null;
    try {
      await SecureStoragePlugin.remove({ key: BEARER_KEY });
    } catch {
      // Removing an absent key is a no-op success; swallow the plugin's not-found throw.
    }
  }
}

/** Pick the provider for the running platform (Capacitor native → Keychain; browser → in-memory). */
export function createTokenProvider(): TokenProvider {
  return Capacitor.isNativePlatform() ? new NativeTokenProvider() : new WebTokenProvider();
}
